'use client';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu';
import { cva } from 'class-variance-authority';

import { cn } from '@/lib/utils';

export function mergeReferences<T = unknown>(
    references: Array<React.MutableRefObject<T> | React.LegacyRef<T>>,
): React.RefCallback<T> {
    return value => {
        for (const reference of references) {
            if (typeof reference === 'function') {
                reference(value);
            } else if (reference != null) {
                (reference as React.MutableRefObject<T | null>).current = value;
            }
        }
    };
}

const NavigationMenu = React.forwardRef<
    React.ElementRef<typeof NavigationMenuPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Root>
>(({ className, children, ...props }, ref) => {
    const containerReference = useRef<HTMLElement>(null);
    const viewportReference = useRef<HTMLDivElement>(null);
    const [isFullWidth, setIsFullWidth] = useState<boolean | null>(null);

    useEffect(() => {
        const container = containerReference.current;
        const viewport = viewportReference.current;
        const gap = 40;

        if (!container) return;

        const updatePosition = (item: HTMLElement) => {
            const menuItemRect = item.getBoundingClientRect();
            const screenSize = window.innerWidth;
            const menuWith = 640;

            const containerRect = container.getBoundingClientRect();

            const itemPosition = {
                top: menuItemRect.top - containerRect.top,
                left: menuItemRect.left - containerRect.left,
            };

            let leftPosition = itemPosition.left - gap;

            if (menuItemRect.left + menuWith > screenSize - gap) {
                leftPosition = (containerRect.left + menuWith + gap - screenSize) * -1;
            }

            container.style.setProperty('--radix-navigation-menu-item-active-left', `${leftPosition}px`);
        };

        const mutationCallback = (mutationsList: MutationRecord[]) => {
            for (const mutation of mutationsList) {
                if (
                    mutation.type === 'attributes' &&
                    mutation.attributeName === 'data-state' &&
                    mutation.target instanceof HTMLElement &&
                    mutation.target.hasAttribute('aria-expanded') &&
                    mutation.target.dataset.state === 'open'
                ) {
                    updatePosition(mutation.target);
                }
                if (
                    mutation.type === 'attributes' &&
                    mutation.attributeName === 'data-state' &&
                    mutation.target instanceof HTMLElement &&
                    mutation.target.hasAttribute('aria-expanded') &&
                    mutation.target.dataset.state === 'closed'
                ) {
                    setIsFullWidth(null);
                }
            }
        };
        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                const menu = entry.target.querySelector('#NavigationMenuContent');
                if (menu) {
                    const fullWith = menu.getAttribute('data-fullwith') === 'true';
                    setIsFullWidth(fullWith);
                    break;
                }
            }
        });

        if (viewport) {
            resizeObserver.observe(viewport);
        }

        const observer = new MutationObserver(mutationCallback);

        observer.observe(container, {
            childList: true,
            attributes: true,
            subtree: true,
            attributeFilter: ['data-state'],
        });

        return () => {
            observer.disconnect();
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <NavigationMenuPrimitive.Root
            ref={mergeReferences([ref, containerReference])}
            delayDuration={0}
            skipDelayDuration={0}
            className={cn(
                'flex max-w-max flex-1 items-center justify-center print:hidden',
                { relative: !isFullWidth },
                className,
            )}
            {...props}>
            {children}

            <NavigationMenuViewport fullWith={isFullWidth} ref={viewportReference} id='NavigationMenuViewport' />
        </NavigationMenuPrimitive.Root>
    );
});
NavigationMenu.displayName = NavigationMenuPrimitive.Root.displayName;

const NavigationMenuList = React.forwardRef<
    React.ElementRef<typeof NavigationMenuPrimitive.List>,
    React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.List>
>(({ className, ...props }, ref) => (
    <NavigationMenuPrimitive.List
        ref={ref}
        className={cn('group flex list-none items-center justify-center', className)}
        {...props}
    />
));
NavigationMenuList.displayName = NavigationMenuPrimitive.List.displayName;

const NavigationMenuItem = React.forwardRef<
    React.ElementRef<typeof NavigationMenuPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Item>
>(({ className, children, ...props }, ref) => {
    return (
        <NavigationMenuPrimitive.Item ref={ref} className={cn(className)} {...props}>
            {children}
        </NavigationMenuPrimitive.Item>
    );
});

NavigationMenuItem.displayName = NavigationMenuPrimitive.List.displayName;

const navigationMenuTriggerStyle = cva(
    'group inline-flex  items-center justify-center  disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 ',
);

const NavigationMenuTrigger = React.forwardRef<
    React.ElementRef<typeof NavigationMenuPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
    <NavigationMenuPrimitive.Trigger
        ref={ref}
        className={cn(navigationMenuTriggerStyle(), 'group peer', className)}
        {...props}>
        {children}
    </NavigationMenuPrimitive.Trigger>
));
NavigationMenuTrigger.displayName = NavigationMenuPrimitive.Trigger.displayName;

const NavigationMenuContent = React.forwardRef<
    React.ElementRef<typeof NavigationMenuPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Content>
>(({ className, ...props }, ref) => (
    <NavigationMenuPrimitive.Content
        ref={ref}
        className={cn('top-0 left-0 w-full md:absolute md:w-auto', className)}
        {...props}
    />
));
NavigationMenuContent.displayName = NavigationMenuPrimitive.Content.displayName;

const NavigationMenuLink = NavigationMenuPrimitive.Link;

const NavigationMenuViewport = React.forwardRef<
    React.ElementRef<typeof NavigationMenuPrimitive.Viewport>,
    React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Viewport> & { fullWith?: boolean | null }
>(({ className, children, fullWith, ...props }, ref) => {
    return (
        <div
            ref={ref}
            className={cn(
                'absolute top-full left-0 z-30 flex w-full transform justify-center overflow-y-hidden pb-10',
                {
                    'top-14 w-auto translate-x-[var(--radix-navigation-menu-item-active-left)]': !fullWith,
                },
            )}>
            <NavigationMenuPrimitive.Viewport
                className={cn(
                    'relative h-[var(--radix-navigation-menu-viewport-height)] w-full max-w-[1440px] overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.16)]',
                    {
                        'w-[var(--radix-navigation-menu-viewport-width)]': fullWith === false,
                    },

                    className,
                )}
                {...props}>
                {children}
            </NavigationMenuPrimitive.Viewport>
        </div>
    );
});
NavigationMenuViewport.displayName = NavigationMenuPrimitive.Viewport.displayName;

export {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuList,
    NavigationMenuTrigger,
    navigationMenuTriggerStyle,
    NavigationMenuViewport,
};
